import { Employee } from '@models/Employee'
import { employeeSchema } from '@models/schema'
import { createSelector, createSlice } from '@reduxjs/toolkit'
import {
  createEmployee as createEmployeeAPI,
  deleteEmployee as deleteEmployeeAPI,
  fetchEmployeeById,
  fetchEmployees,
  updateEmployee as updateEmployeeAPI,
} from '@services/EmployeeService'
import {
  baseReducers,
  getIdOrModelId,
  SBAPI,
  SBSelectRaw,
  SBState,
} from '@supplement-bacon/react-toolkit/utils'
import { initialQuery, TableParams } from '@supplement-bacon/sb-table-filters'
import { AppDispatch, RootState } from './store'

const initialState: SBState<Employee> = {
  isLoading: false,
  error: null,
  items: {},
  ids: [],
  selectedId: undefined,
  query: initialQuery,
}

const slice = createSlice({
  name: 'employee',
  initialState,
  reducers: baseReducers,
})

// Reducer
export default slice.reducer
export const {
  getItemsSuccess: getEmployeesSuccess,
  setQuery: setEmployeeQuery,
  reset: resetEmployeeState,
  resetQueryAndIds: resetEmployeeQueryAndIds,
} = slice.actions

/**
 * Selectors
 */

const selectRawItems: SBSelectRaw<RootState, { [key: number]: Employee }> = (
  state: RootState
) => state[slice.name].items
const selectRawIds: SBSelectRaw<RootState, number[]> = (state: RootState) =>
  state[slice.name].ids
const selectRawSelectedId: SBSelectRaw<RootState, number | undefined> = (
  state: RootState
) => state[slice.name].selectedId

export const selectEmployees = () =>
  createSelector(
    [selectRawItems, selectRawIds],
    (items, ids) => ids.map((id) => items[id]).filter((i) => i)
    // Filter allow to return only non-null elements
  )
export const selectSelectedEmployee = () =>
  createSelector([selectRawItems, selectRawSelectedId], (items, id) =>
    id !== undefined ? items[id] : undefined
  )
export const selectEmployeeById = (id: number) =>
  createSelector([selectRawItems], (items) =>
    items.hasOwnProperty(id) ? items[id] : undefined
  )
export const selectEmployeesByIds = (ids: number[]) =>
  createSelector([selectRawItems], (items) =>
    ids.filter((id) => items.hasOwnProperty(id)).map((id) => items[id])
  )

/**
 * Actions
 */

export const setSelectedEmployee =
  (employee: Employee | number) => async (dispatch: AppDispatch) =>
    dispatch(slice.actions.setSelectedId(getIdOrModelId<Employee>(employee)))

export const getEmployees = (params: TableParams) =>
  SBAPI.FetchPaginatedDispatched(
    fetchEmployees(params),
    [employeeSchema],
    slice.actions
  )

export const createEmployee = (employee: Employee) =>
  SBAPI.CreateDispatched(
    createEmployeeAPI(employee),
    employeeSchema,
    slice.actions
  )

export const getEmployeeWithId = (id: number) =>
  SBAPI.FetchDispatched(fetchEmployeeById(id), employeeSchema, slice.actions)

export const updateEmployee = (employee: Employee) =>
  SBAPI.UpdateDispatched<Employee>(
    updateEmployeeAPI(employee),
    employee,
    slice.actions
  )

export const deleteEmployee = (employee: Employee) =>
  SBAPI.DestroyDispatched<Employee>(
    deleteEmployeeAPI(employee),
    employee,
    slice.actions
  )
